import React from 'react';
import { Form } from 'react-final-form';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_TYPE, INFOBOX_VARIANT, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { GENERIC_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsGenericEvents';
import { TTC_CAR_CHECK_RESULTS_ROUTE } from 'Utilities/routing';

import { isValidVRM, unprettyVRM } from '../../../../checkerPages/Pages/CheckerRouter.utils';
import type { TccCarCheckResponse } from '../../../CarCheckReport.types';
import { CarCheckerVRMField } from '../CarCheckerVRMField/CarCheckerVRMField';

import { CAR_CHECK_SNOWPLOW, getVrmErrorTexts } from './CarCheckerForm.helpers';
import LocalTexts from './CarCheckerFormTexts.json';

import styles from './CarCheckerForm.scss';

const LocalT = new MDText(LocalTexts);

const t = (key: string) => LocalT.translate(key) as string;

export const TEXTS = {
  buttonCTA: 'Check',
  errorMessage: t('hero.vrmForm.errorMessage'),
  label: t('hero.vrmForm.inputLabel'),
};

export const CheckerForm = ({ setTccData }: {
  setTccData: React.Dispatch<React.SetStateAction<TccCarCheckResponse | null>>;
}) => {
  const { ...inputTexts } = TEXTS;

  const onError = () => {
    const { detail, title } = getVrmErrorTexts();

    toast({
      content: detail,
      heading: title,
      important: true,
      variant: INFOBOX_VARIANT.ERROR,
    });
  };

  const formOnSubmit = async ({ carCheckVrm: vrm }: Record<string, string>) => {
    const isVRMValid = vrm && isValidVRM(vrm);
    const trimVRM = isVRMValid && unprettyVRM(vrm);

    if (!isVRMValid) {
      return;
    }

    GENERIC_ANALYTICS_EVENTS.CTA_CLICK({
      ...CAR_CHECK_SNOWPLOW,
      button_label: inputTexts.buttonCTA,
      label: inputTexts.buttonCTA,
      name: 'CarCheckerHero_Check',
      url: TTC_CAR_CHECK_RESULTS_ROUTE,
    });

    setTccData(null);

    const tcc = await fetch(`/api/tcc/car-check?vrm=${trimVRM}`)
      .then((response) => response.json())
      .catch(() => undefined);

    const isSuccess = Boolean(tcc?.data?.BasicVehicleDetailsModel);

    GENERIC_ANALYTICS_EVENTS.SECTION_LOAD_EVENT({
      ...CAR_CHECK_SNOWPLOW,
      label: trimVRM,
      name: isSuccess ? 'CarChecker_Results_Success' : 'CarChecker_Results_Error',
    });

    if (!tcc) {
      onError();
      return;
    }

    setTccData(tcc);
  };

  return (
    <div className={styles.checkerForm}>
      <Form
        onSubmit={formOnSubmit}
        render={({ handleSubmit, submitting }) => (
          <form
            onSubmit={handleSubmit}
          >
            <CarCheckerVRMField
              texts={{ ...inputTexts }}
            />

            <div className={ styles.ctaWrapper }>
              <Button
                data-thc-button
                fullWidth
                icon={'chevron'}
                label={inputTexts.buttonCTA}
                loading={submitting}
                size={SIZE.LARGE}
                type={BUTTON_TYPE.SUBMIT}
                variant={VARIANT.PRIMARY}
              />
            </div>
          </form>
        )}
      />
    </div>
  );
};
